import {
  afterNextRender,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Inner-zoom al pasar el ratón sobre un <img>.
 *
 * Busca el ancestor marcado con `[data-zoom-container]` y escucha mousemove
 * en él, aplicando al propio <img> `transform: scale(F)` con `transform-origin`
 * siguiendo el cursor. Throttled con rAF.
 *
 * Factor `F` adaptativo según `naturalWidth` — si la imagen es muy chica
 * (<500px) el zoom no activa (pixelado inaceptable). En touch devices
 * (sin `hover: hover`) no se adjuntan listeners.
 *
 * Uso:
 *   <div class="wrap" data-zoom-container>
 *     <img [src]="..." appInnerZoom [innerZoomPaused]="isFlipping()" />
 *   </div>
 */
@Directive({
  selector: 'img[appInnerZoom]',
})
export class InnerZoomDirective {
  private readonly el = inject<ElementRef<HTMLImageElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  /** Pausa temporal del efecto (p. ej. durante la animación de flip 3D). */
  innerZoomPaused = input(false);

  private container: HTMLElement | null = null;
  private factor = 0;
  private rafId: number | null = null;
  private pending: { x: number; y: number } | null = null;

  private readonly onMouseMove = (e: MouseEvent) => this.handleMove(e);
  private readonly onMouseLeave = () => this.reset();
  private readonly onLoad = () => this.updateFactor();

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      // Sólo en devices con mouse real. Pointer coarse o sin hover → skip.
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const img = this.el.nativeElement;
      const container = img.closest<HTMLElement>('[data-zoom-container]');
      if (!container) return;
      this.container = container;

      this.updateFactor();
      img.addEventListener('load', this.onLoad);
      container.addEventListener('mousemove', this.onMouseMove, { passive: true });
      container.addEventListener('mouseleave', this.onMouseLeave);

      // `will-change` sólo mientras se monta la directiva — ayuda al
      // compositor a no invalidar layers en cada mousemove.
      img.style.willChange = 'transform';
      img.style.transition = 'transform 150ms ease';

      this.destroyRef.onDestroy(() => {
        img.removeEventListener('load', this.onLoad);
        container.removeEventListener('mousemove', this.onMouseMove);
        container.removeEventListener('mouseleave', this.onMouseLeave);
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
        img.style.willChange = '';
        img.style.transition = '';
        img.style.transform = '';
        img.style.transformOrigin = '';
      });
    });

    // Si la pausa se activa (flip en curso), resetear el transform ya.
    effect(() => {
      if (this.innerZoomPaused()) this.reset();
    });
  }

  private updateFactor(): void {
    const w = this.el.nativeElement.naturalWidth;
    if (w >= 1200) this.factor = 2.8;
    else if (w >= 800) this.factor = 2.2;
    else if (w >= 500) this.factor = 1.8;
    else this.factor = 0;
  }

  private handleMove(e: MouseEvent): void {
    if (this.factor === 0 || this.innerZoomPaused() || !this.container) return;
    const rect = this.container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    this.pending = { x, y };
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (!this.pending) return;
      const img = this.el.nativeElement;
      img.style.transformOrigin = `${this.pending.x}% ${this.pending.y}%`;
      img.style.transform = `scale(${this.factor})`;
      this.pending = null;
    });
  }

  private reset(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const img = this.el.nativeElement;
    img.style.transform = '';
    img.style.transformOrigin = '';
  }
}
